import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import { InvoiceSchema, type InvoiceStatus } from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';

/**
 * Drive an invoice through the server's status state machine.
 * The server enforces legal transitions (`draft -> pending`,
 * `pending -> sent`, etc.) and returns a `ValidationError` on
 * illegal hops; we let it propagate so screens can surface the
 * exact rule that was violated.
 */
export function useTransitionInvoice() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: ({ invoiceId, to }: { invoiceId: string; to: InvoiceStatus }) => {
      if (!orgId) throw new Error('No active organization');
      return postJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/transition`,
        { to },
        InvoiceSchema,
      );
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(
        queryKeys.invoices.detail(invoice.orgId, invoice.id),
        invoice,
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all(invoice.orgId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.activities(invoice.orgId, invoice.id),
      });
    },
  });

  return {
    transitionInvoice: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
